import { useMemo, useState } from 'react';

import { Modal } from '../../components/ui/Modal.jsx';
import { Field } from '../../components/ui/bits.jsx';
import { useUI } from '../../components/ui/feedback.jsx';
import { useSola } from '../../sola.jsx';
import { ytEmbedUrl } from '../../components/ExplanationModal.jsx';
import * as api from '../../lib/api.js';
import { Icon } from '../../components/ui/Icon.jsx';
import { t } from '../../i18n/index.js';

/*
  Urejanje razlag vaj.

  Razlaga obstaja, če ima vsaj eno neprazno polje. Zgoraj je števec —
  razlag je 69 in brez njega ni bilo videti, koliko dela je še pred nami.
*/

const EMPTY = { zakaj: '', kako: '', napake: '', varnost: '', video_url: '', ucbenik_sklic: '' };

const FIELDS = [
  { key: 'zakaj', label: 'Zakaj se izvaja ta vaja?', rows: 3 },
  { key: 'kako', label: 'Kako se izvede pravilno?', rows: 6 },
  { key: 'napake', label: 'Pogoste napake', rows: 3 },
  { key: 'varnost', label: 'Varnostna opozorila', rows: 3 },
];

const has = (e) =>
  !!e && (e.zakaj || e.kako || e.napake || e.varnost || e.video_url || e.ucbenik_sklic);

export function ContentEditor({ explanations, onSaved }) {
  const [editing, setEditing] = useState(null);
  const { program, sola } = useSola();
  const ALL_EX = program.all;

  const stats = useMemo(() => {
    let slots = 0;
    let filled = 0;
    ALL_EX.forEach((ex) => {
      slots += 1 + ex.subs.length;
      if (has(explanations[`${ex.id}:-1`])) filled += 1;
      ex.subs.forEach((_, si) => {
        if (has(explanations[`${ex.id}:${si}`])) filled += 1;
      });
    });
    return { slots, filled };
  }, [explanations, ALL_EX]);

  return (
    <div className="stack">
      <section className="card card--brand card--pad row">
        <span className="spacer">
          <h2 className="t-lg">{t('Razlage vaj')}</h2>
          <p className="t-sm t-dim">
            {t('Klikni na vajo ali pod-vajo za urejanje. Zelena kljukica pomeni, da razlaga obstaja.')}
          </p>
        </span>
        <span className="t-xl t-black t-brand num">
          {stats.filled}/{stats.slots}
        </span>
      </section>

      <section className="card">
        {program.sklopi.map(({ key, label, list }) => (
          <div key={key}>
            <div className="section-label">{label}</div>
            {list.map((ex) => (
              <div key={ex.id} style={{ borderBottom: '1px solid var(--border)' }}>
                <div className="row" style={{ padding: 'var(--s-3) var(--s-4)', background: 'var(--surface-2)' }}>
                  <strong className="spacer t-md">{ex.title}</strong>
                  <Chip
                    on={has(explanations[`${ex.id}:-1`])}
                    label={t('Razlaga vaje')}
                    onClick={() => setEditing({ exId: ex.id, subIndex: -1 })}
                  />
                </div>

                {ex.subs.map((sub, si) => (
                  <div
                    key={si}
                    className="row"
                    style={{
                      padding: 'var(--s-2) var(--s-4) var(--s-2) var(--s-6)',
                      borderTop: '1px solid var(--border)',
                    }}
                  >
                    <span className="spacer t-sm t-dim">{sub}</span>
                    <Chip
                      on={has(explanations[`${ex.id}:${si}`])}
                      label={t('Razlaga')}
                      onClick={() => setEditing({ exId: ex.id, subIndex: si })}
                    />
                  </div>
                ))}
              </div>
            ))}
          </div>
        ))}
      </section>

      {editing && (
        <Editor
          target={editing}
          solaId={sola.id}
          allEx={ALL_EX}
          initial={explanations[`${editing.exId}:${editing.subIndex}`] ?? EMPTY}
          onClose={() => setEditing(null)}
          onSaved={onSaved}
        />
      )}
    </div>
  );
}

function Chip({ on, label, onClick }) {
  return (
    <button className={`btn btn--sm ${on ? 'btn--ok' : 'btn--secondary'}`} onClick={onClick}>
      <Icon name={on ? 'check' : 'plus'} size={14} /> {label}
    </button>
  );
}

function Editor({ target, solaId, allEx, initial, onClose, onSaved }) {
  const { toast } = useUI();
  const [form, setForm] = useState({ ...EMPTY, ...initial });
  const [saving, setSaving] = useState(false);

  const ex = allEx.find((e) => e.id === target.exId);
  const subTitle = target.subIndex >= 0 ? ex?.subs[target.subIndex] : '';
  const embed = ytEmbedUrl(form.video_url);
  const badVideo = form.video_url && !embed;

  const save = async () => {
    setSaving(true);
    try {
      await api.saveExerciseContent(solaId, target.exId, target.subIndex, form);
      await onSaved();
      toast.ok(t('Razlaga je shranjena.'));
      onClose();
    } catch (e) {
      toast.bad(e.message ?? t('Shranjevanje ni uspelo.'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      eyebrow={target.subIndex === -1 ? t('Razlaga vaje') : t('Pod-vaja {n}', { n: target.subIndex + 1 })}
      title={`${ex?.title}${subTitle ? ' · ' + subTitle : ''}`}
      onClose={onClose}
      footer={
        <>
          <button className="btn btn--primary" onClick={save} disabled={saving}>
            {saving ? t('Shranjujem …') : t('Shrani')}
          </button>
          <button className="btn btn--ghost" onClick={onClose}>
            {t('Prekliči')}
          </button>
        </>
      }
    >
      <div className="stack">
        {FIELDS.map((f) => (
          <Field key={f.key} label={t(f.label)} id={`c-${f.key}`}>
            <textarea
              id={`c-${f.key}`}
              className="input"
              rows={f.rows}
              value={form[f.key]}
              data-autofocus={f.key === 'zakaj' ? '' : undefined}
              onChange={(e) => setForm((p) => ({ ...p, [f.key]: e.target.value }))}
            />
          </Field>
        ))}

        <Field
          label={t('Povezava do videa (YouTube)')}
          hint={t('Podpira watch?v=…, youtu.be/…, shorts/…')}
          error={badVideo ? t('Te povezave ne prepoznam kot YouTube. Video se ne bo prikazal.') : null}
          id="c-video"
        >
          <input
            id="c-video"
            className="input"
            value={form.video_url}
            placeholder="https://www.youtube.com/watch?v=…"
            aria-invalid={badVideo ? 'true' : undefined}
            onChange={(e) => setForm((p) => ({ ...p, video_url: e.target.value }))}
          />
        </Field>

        {embed && (
          <div
            style={{
              position: 'relative',
              paddingBottom: '56.25%',
              borderRadius: 'var(--r-md)',
              overflow: 'hidden',
              background: '#000',
            }}
          >
            <iframe
              src={embed}
              title={t('Predogled videa')}
              style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', border: 0 }}
              allowFullScreen
            />
          </div>
        )}

        <Field label={t('Sklic na učbenik')} id="c-book">
          <input
            id="c-book"
            className="input"
            value={form.ucbenik_sklic}
            placeholder={t('npr. 4. poglavje – Vzletna tehnika, str. 78')}
            onChange={(e) => setForm((p) => ({ ...p, ucbenik_sklic: e.target.value }))}
          />
        </Field>
      </div>
    </Modal>
  );
}
